import { WebinarErrorMessages } from 'react-scale/components/WebinarSessions/constants';
import {
  createWebinarSessionStore,
  CreateWebinarSessionStore,
  CreateWebinarSessionStoreProvider,
} from 'react-scale/components/WebinarSessions/hooks/useCreateWebinarSessionStore';
import { useState, useRef, useEffect } from 'react';
import { Button, Flex, Tooltip } from '@scale/scaleui-radix';
import { isNil } from 'lodash';
import { useMutation } from '@tanstack/react-query-v4';
import { CreateSessionSuccessModal } from 'react-scale/components/WebinarSessions/components/CreateSessionSuccessModal';
import { useToggle } from 'react-use-v13';
import { useRouteMatch } from 'react-router-dom';
import { useStore, StoreApi } from 'zustand';
import WebinarFormLayout, {
  NAVIGATION_ITEMS,
} from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarFormLayout';
import { useSearchUsers } from 'react-scale/components/WebinarSessions/api';
import WebinarSettingsForm from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarSettingsForm';
import WebinarInviteeForm from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarInviteeForm';
import WebinarInviteesSummary from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarInviteesSummary';
import WebinarSessionsForm from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarSessionsForm';
import { RecurringType } from '@scale/shared/webinarSessions/types';
import { useUser } from 'react-scale/components/ui/UserContext';

const useCreateWebinarSessions = ({
  store,
  onSuccessFn,
}: {
  store: StoreApi<CreateWebinarSessionStore>;
  onSuccessFn: () => void;
}) => {
  const createSessions = useStore(store, state => state.createSessions);

  return useMutation({
    mutationFn: createSessions,
    onSuccess: () => {
      onSuccessFn();
    },
  });
};

const CreateWebinarView = () => {
  const { params, url } = useRouteMatch<{ projectId: string }>();
  const urlParts = url.split('/');
  const webinarsUrl = urlParts.slice(0, urlParts.length - 1).join('/');

  const opsHubMatch = useRouteMatch<{ projectId: string }>(
    '*/projects/:projectId/enablement/webinar-sessions',
  );
  const projectId = params.projectId || opsHubMatch?.params.projectId;
  const user = useUser();
  const isProjectCreation = !isNil(projectId);
  const store = useRef(
    createWebinarSessionStore({
      project: projectId,
      isProjectCreation,
      sessionType: RecurringType.MULTI,
      isV2Creation: true,
      hostedBy: user ? [user.id] : [],
      excludeEmptyQueueUsers: isProjectCreation,
    }),
  ).current;
  const {
    name,
    setName,
    description,
    setDescription,
    eventStart,
    setEventStart,
    duration,
    setDuration,
    hostedBy,
    setHostedBy,
    rsvpMax,
    setRsvpMax,
    minAttendanceDuration,
    setMinAttendanceDuration,
    expectedNumberOfAttendees,
    setExpectedNumberOfAttendees,
    project,
    setProject,
    sessionType,
    setSessionType,
    invitedWorkerTeams,
    setInvitedWorkerTeams,
    invitedUsers,
    setInvitedUsers,
    invitedSquads,
    setInvitedSquads,
    invitedQualifications,
    setInvitedQualifications,
    audience,
    setAudience,
    audienceBucket,
    setAudienceBucket,
    allInvitedUserIds,
    setAllInvitedUserIds,
    sendInvitationEmails,
    setSendInvitationEmails,
    send30MinuteReminderEmails,
    setSend30MinuteReminderEmails,
    excludeEmptyQueueUsers,
    setExcludeEmptyQueueUsers,
    numOccurrences,
    setNumOccurrences,
    selectedDate,
    setSelectedDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    repeatNumber,
    setRepeatNumber,
    repeatType,
    setRepeatType,
    daysOfTheWeek,
    setDaysOfTheWeek,
    additionalSessions,
    setAdditionalSessions,
    occurencesV2,
    setOccurencesV2,
    additionalTimesOfDayV2,
    setAdditionalTimesOfDayV2,
    getErrors,
    getRecurringErrors,
    getAdditionalSessionsErrors,
    newlyCreatedSessions,
    cohortMappings,
    setCohortMappings,
  } = useStore(store);

  const [sectionIndex, setSectionIndex] = useState<number>(0);
  const [showCreateSessionSuccessModal, toggleCreateSessionSuccessModal] = useToggle(false);
  const { mutate: createWebinarSessions, isLoading: isCreatingWebinarSessions } =
    useCreateWebinarSessions({
      store,
      onSuccessFn: () => {
        toggleCreateSessionSuccessModal();
      },
    });

  const { data: searchUsersResponse, isLoading: isLoadingAllInvitedUserIds } = useSearchUsers({
    userIds: invitedUsers,
    workerTeamIds: invitedWorkerTeams,
    squadIds: invitedSquads,
    qualificationIds: invitedQualifications,
    audienceId: audience ?? undefined,
    audienceBucket: audienceBucket ?? undefined,
    excludeEmptyQueueUsers: excludeEmptyQueueUsers ?? undefined,
    cohortVersionIds: cohortMappings.map(m => m.cohortVersionId).filter((id): id is string => !!id),
  });

  useEffect(() => {
    if (!isLoadingAllInvitedUserIds) {
      setAllInvitedUserIds(searchUsersResponse?.userIds ?? []);
    }
  }, [searchUsersResponse, isLoadingAllInvitedUserIds, setAllInvitedUserIds]);

  const webinarErrors = getErrors();

  const footerContent = (
    <Flex justify="end" gap="2">
      {sectionIndex > 0 && (
        <Button
          size="2"
          variant="outline"
          color="neutral"
          radius="small"
          disabled={sectionIndex === 0 || isCreatingWebinarSessions}
          onClick={() => setSectionIndex(sectionIndex - 1)}
        >
          Previous
        </Button>
      )}
      {sectionIndex !== NAVIGATION_ITEMS.length - 1 && (
        <Button
          size="2"
          variant="solid"
          color="accent"
          radius="small"
          onClick={() => setSectionIndex(sectionIndex + 1)}
          disabled={isCreatingWebinarSessions}
        >
          Next
        </Button>
      )}
      {sectionIndex === NAVIGATION_ITEMS.length - 1 && (
        <Tooltip
          content={
            webinarErrors.length > 0 ? WebinarErrorMessages[webinarErrors[0]] : 'Create Webinar'
          }
          side="bottom"
        >
          <Button
            size="2"
            variant="solid"
            color="accent"
            radius="small"
            disabled={webinarErrors.length > 0}
            onClick={() => createWebinarSessions()}
            loading={isCreatingWebinarSessions}
          >
            Create Webinar
          </Button>
        </Tooltip>
      )}
    </Flex>
  );

  return (
    <CreateWebinarSessionStoreProvider store={store}>
      <WebinarFormLayout
        sectionIndex={sectionIndex}
        setSectionIndex={setSectionIndex}
        webinarName={name ?? ''}
        backUrl={webinarsUrl}
        backText="Back to webinar sessions"
        webinarSettingsContent={
          <WebinarSettingsForm
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
            project={project}
            setProject={setProject}
            rsvpMax={rsvpMax}
            setRsvpMax={setRsvpMax}
            minAttendanceDuration={minAttendanceDuration}
            setMinAttendanceDuration={setMinAttendanceDuration}
            expectedNumberOfAttendees={expectedNumberOfAttendees}
            setExpectedNumberOfAttendees={setExpectedNumberOfAttendees}
            webinarErrors={webinarErrors}
            duration={duration}
            isProjectView={!!projectId}
          />
        }
        sessionSettingsContent={
          <WebinarSessionsForm
            sessionType={sessionType}
            setSessionType={setSessionType}
            setDuration={setDuration}
            eventStart={eventStart}
            setEventStart={setEventStart}
            numOccurrences={numOccurrences}
            setNumOccurrences={setNumOccurrences}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
            hostedBy={hostedBy}
            setHostedBy={setHostedBy}
            repeatNumber={repeatNumber}
            setRepeatNumber={setRepeatNumber}
            repeatType={repeatType}
            setRepeatType={setRepeatType}
            daysOfTheWeek={daysOfTheWeek}
            setDaysOfTheWeek={setDaysOfTheWeek}
            additionalSessions={additionalSessions}
            setAdditionalSessions={setAdditionalSessions}
            occurencesV2={occurencesV2}
            setOccurencesV2={setOccurencesV2}
            additionalTimesOfDayV2={additionalTimesOfDayV2}
            setAdditionalTimesOfDayV2={setAdditionalTimesOfDayV2}
            getErrors={getErrors}
            getRecurringErrors={getRecurringErrors}
            getAdditionalSessionsErrors={getAdditionalSessionsErrors}
          />
        }
        selectInviteeContent={
          <WebinarInviteeForm
            project={project}
            invitedWorkerTeams={invitedWorkerTeams}
            setInvitedWorkerTeams={setInvitedWorkerTeams}
            invitedUsers={invitedUsers}
            setInvitedUsers={setInvitedUsers}
            invitedQualifications={invitedQualifications}
            setInvitedQualifications={setInvitedQualifications}
            invitedSquads={invitedSquads}
            setInvitedSquads={setInvitedSquads}
            audience={audience}
            setAudience={setAudience}
            audienceBucket={audienceBucket}
            setAudienceBucket={setAudienceBucket}
            webinarErrors={webinarErrors}
            excludeEmptyQueueUsers={excludeEmptyQueueUsers}
            setExcludeEmptyQueueUsers={setExcludeEmptyQueueUsers}
            cohortMappings={cohortMappings}
            setCohortMappings={setCohortMappings}
          />
        }
        inviteeSummaryContent={
          <WebinarInviteesSummary
            invitedUsers={invitedUsers}
            setInvitedUsers={setInvitedUsers}
            sendInvitationEmails={sendInvitationEmails}
            setSendInvitationEmails={setSendInvitationEmails}
            send30MinuteReminderEmails={send30MinuteReminderEmails}
            setSend30MinuteReminderEmails={setSend30MinuteReminderEmails}
            allInvitedUserIds={allInvitedUserIds}
          />
        }
        footerContent={footerContent}
      />

      <CreateSessionSuccessModal
        newlyCreatedSessions={newlyCreatedSessions}
        showModal={showCreateSessionSuccessModal}
        toggleModal={toggleCreateSessionSuccessModal}
      />
    </CreateWebinarSessionStoreProvider>
  );
};

export default CreateWebinarView;
